import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useUser } from '@clerk/clerk-react';
import { useReportMetrics, useOrdersByStage } from '../hooks/useReports.js';
import { useOrders } from '../hooks/useOrders.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import { useFeatureFlag } from '../lib/posthog.js';

// ── Formatting helpers ─────────────────────────────────────────────────────────

export function formatDashboardCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatDashboardDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

// ── Status colours ─────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  draft: 'var(--color-text-muted)',
  in_production: 'var(--color-brand)',
  complete: 'var(--color-success)',
  cancelled: 'var(--color-danger)',
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  isLoading,
  isError,
}: {
  label: string;
  value: string | number;
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <div
      style={{
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-5)',
        boxShadow: 'var(--shadow-xs)',
      }}
    >
      <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>
        {label}
      </p>
      {isLoading ? (
        <div className="skeleton-line" style={{ width: 80, height: 32, borderRadius: 'var(--radius-sm)' }} />
      ) : isError ? (
        <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-muted)' }}>—</p>
      ) : (
        <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text)' }}>{value}</p>
      )}
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { t } = useTranslation('dashboard');
  const { t: tOrders } = useTranslation('orders');
  const { user: clerkUser } = useUser();
  const workspace = useWorkspaceStore((s) => s.workspace);
  const navigate = useNavigate();

  const dashboardEnabled = useFeatureFlag('dashboard');

  // Always call hooks — conditionally render below
  const { data: metrics, isLoading: metricsLoading, isError: metricsError } = useReportMetrics({});
  const { data: byStage = [], isLoading: stageLoading } = useOrdersByStage({});
  const { data: recentData, isLoading: recentLoading } = useOrders({
    limit: 5,
    sortBy: 'created_at',
    sortDir: 'desc',
  });

  const recentOrders = recentData?.data ?? [];
  const currency = workspace?.currency ?? 'USD';
  const firstName = clerkUser?.firstName;

  if (!dashboardEnabled) {
    return (
      <div className="page-enter">
        <h1 className="page-title" style={{ marginBottom: 'var(--space-2)' }}>
          {t('title')}
        </h1>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
          {firstName ? t('welcome', { name: firstName }) : t('welcome_no_name')}
        </p>
      </div>
    );
  }

  return (
    <div className="page-enter">
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 className="page-title" style={{ marginBottom: 'var(--space-1)' }}>
          {t('title')}
        </h1>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
          {firstName ? t('welcome', { name: firstName }) : t('welcome_no_name')}
        </p>
      </div>

      {/* Metric cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <MetricCard
          label={t('metrics.total_orders')}
          value={metrics?.totalOrders ?? 0}
          isLoading={metricsLoading}
          isError={metricsError}
        />
        <MetricCard
          label={t('metrics.total_value')}
          value={metrics ? formatDashboardCurrency(metrics.totalValue, currency) : '—'}
          isLoading={metricsLoading}
          isError={metricsError}
        />
        <MetricCard
          label={t('metrics.open_orders')}
          value={metrics?.openOrders ?? 0}
          isLoading={metricsLoading}
          isError={metricsError}
        />
        <MetricCard
          label={t('metrics.due_this_week')}
          value={metrics?.ordersDueThisWeek ?? 0}
          isLoading={metricsLoading}
          isError={metricsError}
        />
      </div>

      {/* Error state */}
      {metricsError && (
        <p style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)' }}>
          {t('error')}
        </p>
      )}

      {/* Two-column layout: chart + recent orders */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.6fr)',
          gap: 'var(--space-5)',
          alignItems: 'start',
        }}
      >
        {/* Orders by stage chart */}
        <div
          style={{
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-5)',
            boxShadow: 'var(--shadow-xs)',
          }}
        >
          <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)', marginBottom: 'var(--space-4)' }}>
            {t('orders_by_stage.title')}
          </p>

          {stageLoading ? (
            <div className="skeleton-line" style={{ height: 160, borderRadius: 'var(--radius-md)' }} />
          ) : byStage.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: 'var(--space-8) 0', textAlign: 'center' }}>
              {t('orders_by_stage.empty')}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={byStage} barSize={20} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis
                  dataKey="stageName"
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  tickLine={false}
                  axisLine={false}
                  width={80}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--color-bg)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 12,
                  }}
                  cursor={{ fill: 'var(--color-bg-subtle)' }}
                />
                <Bar dataKey="count" fill="var(--color-brand)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Recent orders */}
        <div
          style={{
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-xs)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4) var(--space-5)',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)' }}>
              {t('recent_orders.title')}
            </p>
            <button
              onClick={() => navigate('/orders')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-xs)', color: 'var(--color-brand)', padding: 0 }}
            >
              {t('recent_orders.view_all')}
            </button>
          </div>

          {recentLoading ? (
            <div style={{ padding: 'var(--space-4)' }}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton-line" style={{ height: 44, marginBottom: 8, borderRadius: 'var(--radius-md)' }} />
              ))}
            </div>
          ) : recentOrders.length === 0 ? (
            <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
                {t('recent_orders.empty_title')}
              </p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                {t('recent_orders.empty_subtitle')}
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--color-bg-subtle)' }}>
                  {[
                    t('recent_orders.col_order'),
                    t('recent_orders.col_name'),
                    t('recent_orders.col_status'),
                    t('recent_orders.col_due'),
                    t('recent_orders.col_total'),
                  ].map((col) => (
                    <th
                      key={col}
                      style={{
                        padding: 'var(--space-2) var(--space-4)',
                        textAlign: 'left',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--color-text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order, idx) => (
                  <tr
                    key={order.id}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    style={{
                      cursor: 'pointer',
                      borderTop: idx > 0 ? '1px solid var(--color-border)' : 'none',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-bg-subtle)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
                  >
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-brand)', whiteSpace: 'nowrap' }}>
                      <Link
                        to={`/orders/${order.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {order.orderNumber ?? order.name}
                      </Link>
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {order.name}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', color: STATUS_COLORS[order.status] ?? 'var(--color-text-muted)' }}>
                        {tOrders(`status.${order.status}`, { defaultValue: order.status })}
                      </span>
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                      {formatDashboardDate(order.dueDate)}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text)', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {order.total ? formatDashboardCurrency(Number(order.total), currency) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
